import ReactDOM from "react-dom";
import swal from "sweetalert";
import RecurringShiftSwitch from "pages/Rostering/WorkerShift/RecurringShiftSwitch";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";

const SweetAlertConfirmationModal = (props) => {
  const {
    title,
    dangerMode = false,
    setIsConfirm,
    isOpen = false,
    shiftData = null,
    content,
    setIsRecurringShift,
    onlyOkBtn = null,
    elevatedZIndex = false,
  } = props;

  const { watch, setValue, control } = useForm();
  const watchFormData = watch();

  const [recurringEffectedShiftCount, setRecurringEffectedShiftCount] =
    useState(null);
  const [loading, setLoading] = useState(false);

  const contentContainerRef = useRef(null);
  const swalOpenRef = useRef(false);
  // Ignore promise resolution from programmatic swal.close() during content updates.
  const ignoreNextDismissRef = useRef(false);
  const setIsConfirmRef = useRef(setIsConfirm);

  useEffect(() => {
    setIsConfirmRef.current = setIsConfirm;
  }, [setIsConfirm]);

  const applyElevatedZIndex = () => {
    const bump = (attempt = 0) => {
      const overlays = document.querySelectorAll(".swal-overlay");
      const modals = document.querySelectorAll(".swal-modal");
      if (!overlays.length && !modals.length && attempt < 20) {
        requestAnimationFrame(() => bump(attempt + 1));
        return;
      }
      // Must use !important to override index.css (.swal-overlay { z-index: 10050 !important })
      // so past-shift / similar confirms appear above advance fullscreen modal (z-[99999]).
      overlays.forEach((el) => {
        el.style.setProperty("z-index", "100001", "important");
      });
      modals.forEach((el) => {
        el.style.setProperty("z-index", "100002", "important");
      });
    };
    bump();
  };

  const closeSwalQuietly = () => {
    if (!swalOpenRef.current) return;
    ignoreNextDismissRef.current = true;
    try {
      // sweetalert.close() is onAction; it throws if actions are unset
      // (idle modal, failed open, or already dismissed).
      if (swal.getState?.()?.isOpen) {
        swal.close();
      }
    } catch (_) {
      // ignore — never let dismiss crash the host page
    } finally {
      swalOpenRef.current = false;
    }
  };

  /** sweetalert treats buttons:["OK"] as cancel-only (no confirm). Prefer string/object. */
  const resolveButtons = () => {
    if (onlyOkBtn == null) {
      return ["No, take me back!", "Yes, go ahead!"];
    }
    if (Array.isArray(onlyOkBtn)) {
      if (onlyOkBtn.length === 1) {
        return String(onlyOkBtn[0] ?? "OK");
      }
      if (onlyOkBtn.length === 2) {
        return onlyOkBtn;
      }
    }
    return onlyOkBtn;
  };

  const renderRecurringContent = () => {
    if (!contentContainerRef.current) {
      contentContainerRef.current = document.createElement("div");
    }

    ReactDOM.render(
      <div>
        <RecurringShiftSwitch
          control={control}
          watchFormData={watchFormData}
          selectedShift={shiftData}
          setValue={setValue}
          recurringEffectedShiftCount={recurringEffectedShiftCount}
          setRecurringEffectedShiftCount={setRecurringEffectedShiftCount}
          setIsRecurringShift={setIsRecurringShift}
          setLoading={setLoading}
        />
        {(loading ||
          (recurringEffectedShiftCount !== null &&
            recurringEffectedShiftCount !== undefined)) && (
            <p className='mt-5 font-bold text-center'>
              {loading
                ? "Loading..."
                : recurringEffectedShiftCount > 0
                ? `${recurringEffectedShiftCount} shift(s) will be deleted`
                : "There are no other recurring shifts associated with this shift."}
            </p>
          )}
      </div>,
      contentContainerRef.current
    );

    return contentContainerRef.current;
  };

  const handleConfirmResult = (willDelete) => {
    swalOpenRef.current = false;

    if (ignoreNextDismissRef.current) {
      ignoreNextDismissRef.current = false;
      return;
    }

    // Always use latest parent callback so recurring_impact toggle state is current.
    if (typeof setIsConfirmRef.current === "function") {
      setIsConfirmRef.current(!!willDelete);
    }
  };

  // Open / close swal only when isOpen changes — never on toggle/count updates.
  useEffect(() => {
    if (!isOpen) {
      closeSwalQuietly();
      setRecurringEffectedShiftCount(null);
      setLoading(false);
      setValue("recurring_impact", false);
      return undefined;
    }

    // content === null → recurring delete confirm with series toggle
    if (content !== null && content !== undefined) {
      swalOpenRef.current = true;
      const opened = swal({
        title: title,
        text: content,
        buttons: resolveButtons(),
        dangerMode: dangerMode,
        closeOnClickOutside: false,
      });
      if (opened && typeof opened.then === "function") {
        opened.then(handleConfirmResult);
      } else {
        swalOpenRef.current = false;
      }
      if (elevatedZIndex) {
        applyElevatedZIndex();
      }
    } else {
      const container = renderRecurringContent();
      swalOpenRef.current = true;
      const opened = swal({
        title: title,
        content: container,
        buttons: ["No, take me back!", "Yes, go ahead!"],
        dangerMode: dangerMode,
        closeOnClickOutside: false,
      });
      if (opened && typeof opened.then === "function") {
        opened.then(handleConfirmResult);
      } else {
        swalOpenRef.current = false;
      }
      if (elevatedZIndex) {
        applyElevatedZIndex();
      }
    }

    return () => {
      if (contentContainerRef.current) {
        ReactDOM.unmountComponentAtNode(contentContainerRef.current);
      }
      closeSwalQuietly();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update recurring toggle UI in-place without closing/reopening the dialog.
  useEffect(() => {
    if (!isOpen || (content !== null && content !== undefined)) return;
    if (!swalOpenRef.current || !contentContainerRef.current) return;
    renderRecurringContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    recurringEffectedShiftCount,
    loading,
    watchFormData?.recurring_impact,
  ]);

  return null;
};

export default SweetAlertConfirmationModal;
